import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';
import { CreateSkeletons, NotificationSkeleton } from '@components/ProductSkeleton';
import { toastError } from '@components/Toast';

import { NotificationItem } from '@features/notification/components/NotificationItem';
import {
	ACTIVE_TAB_OBJ,
	ACTIVE_VALUE,
	DATA_LOADED_ONCE,
	ERROR_MESSAGE,
	FILTER_ACTION,
	HAS_NEXT,
	NOTIFICATION_FILTER_NAME,
	NOTIFICATION_TAB_NAME,
	NOTIFICATIONS,
	PAGE,
	UNSEEN_COUNT,
	UNSEEN_COUNT_OBJ
} from '@features/notification/utils/Utils';

import { _storeData } from '@store/actions/NotificationAction';
import { getNotification } from '@store/reducers';

import { fetchUserByEmail } from '@services/commonService';

import ApiClient from '@lib/apiClient';

const Notification = ({ toggleModel, isToggleModel }) => {
	const [memberList, setMemberList] = useState([]);
	let [isLoading, setIsLoading] = useState(false);
	const notification = useSelector(getNotification);
	const notificationPanelRef = useRef(null);
	const dispatch = useDispatch();

	const fetchMemberList = async () => {
		try {
			const data = await fetchUserByEmail(`?isNitexUser=true&page=0&size=500`);
			let userList = [];
			for (let item of data) {
				userList.push({
					email: item.email,
					id: item.id,
					memberId: item.id,
					value: item.name
				});
			}
			setMemberList(userList);
		} catch (e) {
			toastError(e?.response?.data?.message);
		}
	};

	useEffect(() => {
		if (!notification.dataLoadedOnce) {
			getAllUnseenCount().then().catch();
		}
		fetchMemberList();
		return () => {};
	}, []);

	useEffect(() => {
		if (!notification.dataLoadedOnce) {
			fetchNotification(0, ACTIVE_TAB_OBJ.mentions.enum, false).then().catch();
		}
		return () => {};
	}, []);

	const onScrollToEnd = () => {
		const wrappedElement = notificationPanelRef.current;
		if (
			Math.abs(wrappedElement.scrollHeight - wrappedElement.scrollTop - wrappedElement.clientHeight) < 5
		) {
			if (notification.hasNext && !isLoading) {
				fetchNotification(
					notification.page + 1,
					getCategory(),
					true,
					notification.notificationFilterName.isSeen,
					notification.notificationFilterName.isImportant
				)
					.then()
					.catch();
			}
		}
	};

	const markAllByFrontEndCategory = async () => {
		setIsLoading(true);
		let event = getCategory();
		let params = event ? `?eventList=${event}` : '';
		let tempNotification = [];
		await ApiClient.POST('markAllNotificationUnreadByCategory', {}, params)
			.then(() => {
				tempNotification = notification.notifications.map((notification) => {
					if (event === null || event === undefined) {
						notification.isSeen = true;
					} else if (notification.frontEndNotificationEvent === event) {
						notification.isSeen = true;
					}
					return notification;
				});
				dispatch(_storeData(NOTIFICATIONS, tempNotification));
				getAllUnseenCount().then().catch();
				setIsLoading(false);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError(ERROR_MESSAGE);
				}
			});
	};

	const fetchNotification = async (page = 0, category, merge = true, isSeen, isImportant) => {
		let size = notification.size;
		let sort = notification.sort;
		let params = {
			page,
			size,
			sort,
			frontEndNotificationEvents: category,
			isSeen: isSeen,
			isImportant: isImportant
		};
		setIsLoading(true);

		let tempNotification = [];
		let result = [];
		let currentPage;
		let totalPage;
		await ApiClient.GET('getNotifications', params)
			.then(({ data }) => {
				if (data && data.data.length !== 0) {
					//console.log('getNotifications SUCCESS: ', data);
					result = data.data;
					currentPage = data.currentPage;
					totalPage = data.totalPages;
					setIsLoading(false);
				} else {
					setIsLoading(false);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError(ERROR_MESSAGE);
				}
			});
		if (merge) {
			tempNotification = [...notification.notifications, ...result];
		} else {
			tempNotification = [...result];
		}
		dispatch(_storeData(DATA_LOADED_ONCE, true));
		dispatch(_storeData(HAS_NEXT, currentPage < totalPage));
		dispatch(_storeData(NOTIFICATIONS, tempNotification));

		if (result.length) {
			dispatch(_storeData(PAGE, page));
		}
	};

	const markRead = async (item, isSeen) => {
		let tempNotification = [];
		setIsLoading(true);
		let groupByKey = item?.groupByKey;
		let params = groupByKey ? `?id=${item?.id}&groupByKey=${item?.groupByKey}` : `?id=${item?.id}`;
		if (isSeen) {
			await ApiClient.POST('markNotificationUnread', {}, params)
				.then(() => {
					setIsLoading(false);

					tempNotification = notification.notifications.map((notification) => {
						if (notification.id === item.id) {
							notification.isSeen = false;
						}
						return notification;
					});
					dispatch(_storeData(NOTIFICATIONS, tempNotification));
					getAllUnseenCount().then().catch();
				})
				.catch(({ response }) => {
					setIsLoading(false);
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError(ERROR_MESSAGE);
					}
				});
		} else {
			await ApiClient.POST('markNotificationRead', {}, params)
				.then(() => {
					setIsLoading(false);
					tempNotification = notification.notifications.map((notification) => {
						if (notification.id === item.id) {
							notification.isSeen = true;
						}
						return notification;
					});
					dispatch(_storeData(NOTIFICATIONS, tempNotification));
					getAllUnseenCount().then().catch();
				})
				.catch(({ response }) => {
					setIsLoading(false);
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError(ERROR_MESSAGE);
					}
				});
		}
	};

	const markImportant = async (item, isImportant) => {
		let tempNotification = [];
		setIsLoading(true);
		let groupByKey = item?.groupByKey;
		let params = groupByKey ? `?id=${item?.id}&groupByKey=${item?.groupByKey}` : `?id=${item?.id}`;
		if (isImportant) {
			await ApiClient.POST('markNotificationImportant', {}, params)
				.then(() => {
					setIsLoading(false);

					tempNotification = notification.notifications.map((notification) => {
						if (notification.id === item.id) {
							notification.isImportant = true;
						}
						return notification;
					});
					dispatch(_storeData(NOTIFICATIONS, tempNotification));
				})
				.catch(({ response }) => {
					setIsLoading(false);
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError(ERROR_MESSAGE);
					}
				});
		} else {
			await ApiClient.POST('markNotificationUnImportant', {}, params)
				.then(() => {
					setIsLoading(false);
					tempNotification = notification.notifications.map((notification) => {
						if (notification.id === item.id) {
							notification.isImportant = false;
						}
						return notification;
					});
					dispatch(_storeData(NOTIFICATIONS, tempNotification));
				})
				.catch(({ response }) => {
					setIsLoading(false);
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError(ERROR_MESSAGE);
					}
				});
		}
	};
	const getCategory = () => {
		return ACTIVE_TAB_OBJ[
			Object.keys(ACTIVE_TAB_OBJ).find(
				(value) => value === notification.notificationTabName.toLowerCase()
			)
		].enum;
	};
	const getAllUnseenCount = async () => {
		await ApiClient.GET('getAllUnseenNotificationCount')
			.then(({ data }) => {
				dispatch(_storeData(UNSEEN_COUNT_OBJ, data));
				dispatch(_storeData(UNSEEN_COUNT, data.mentions));
			})
			.catch(({ response }) => {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError(ERROR_MESSAGE);
				}
			});
	};

	const getActiveTab = (obj) => {
		notificationPanelRef.current.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth'
		});
		dispatch(_storeData(NOTIFICATION_TAB_NAME, obj.value));
		dispatch(_storeData(NOTIFICATION_FILTER_NAME, FILTER_ACTION.all));
		fetchNotification(0, obj.enum, false).then().catch();
	};
	const getCountNumber = (value, isHtmlClassName) => {
		if (value === undefined || value === null) return '';
		else return value > 0 ? (isHtmlClassName ? 'count' : value > 99 ? '99+' : value) : '';
	};

	const getFilterData = (actionValue) => {
		dispatch(_storeData(NOTIFICATION_FILTER_NAME, actionValue));
		fetchNotification(0, getCategory(), false, actionValue.isSeen, actionValue.isImportant)
			.then()
			.catch();
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div
				className="notification notification-section scroll-y-label"
				id="notification-panel"
				ref={notificationPanelRef}
				onScroll={onScrollToEnd}
			>
				<div className="notification-header">
					<div className="notification-tab">
						<ul>
							<li
								className={
									notification.notificationTabName === ACTIVE_TAB_OBJ.all.value
										? ACTIVE_VALUE
										: ''
								}
								onClick={() => getActiveTab(ACTIVE_TAB_OBJ.all)}
							>
								{ACTIVE_TAB_OBJ.all.value}
								<span className={getCountNumber(notification.unseenCountObj?.all, true)}>
									{getCountNumber(notification.unseenCountObj?.all, false)}
								</span>
							</li>
							<li
								className={
									notification.notificationTabName === ACTIVE_TAB_OBJ.mentions.value
										? ACTIVE_VALUE
										: ''
								}
								onClick={() => getActiveTab(ACTIVE_TAB_OBJ.mentions)}
							>
								{ACTIVE_TAB_OBJ.mentions.value}
								<span
									className={getCountNumber(notification?.unseenCountObj?.mentions, true)}
								>
									{getCountNumber(notification?.unseenCountObj?.mentions, false)}
								</span>
							</li>
							<li
								className={
									notification.notificationTabName === ACTIVE_TAB_OBJ.comments.value
										? ACTIVE_VALUE
										: ''
								}
								onClick={() => getActiveTab(ACTIVE_TAB_OBJ.comments)}
							>
								{ACTIVE_TAB_OBJ.comments.value}
								<span className={getCountNumber(notification?.unseenCountObj?.comment, true)}>
									{getCountNumber(notification?.unseenCountObj?.comment, false)}
								</span>
							</li>

							<li
								className={
									notification.notificationTabName === ACTIVE_TAB_OBJ.activities.value
										? ACTIVE_VALUE
										: ''
								}
								onClick={() => getActiveTab(ACTIVE_TAB_OBJ.activities)}
							>
								{ACTIVE_TAB_OBJ.activities.value}
								<span
									className={getCountNumber(notification?.unseenCountObj?.activities, true)}
								>
									{getCountNumber(notification?.unseenCountObj?.activities, false)}
								</span>
							</li>
							<li
								className={
									notification.notificationTabName === ACTIVE_TAB_OBJ.order.value
										? ACTIVE_VALUE
										: ''
								}
								onClick={() => getActiveTab(ACTIVE_TAB_OBJ.order)}
							>
								{ACTIVE_TAB_OBJ.order.value}
								<span className={getCountNumber(notification?.unseenCountObj?.order, true)}>
									{getCountNumber(notification?.unseenCountObj?.order, false)}
								</span>
							</li>
						</ul>
					</div>
					<div className="notification-filter">
						<div className="dropdown">
							<button
								className="button text-only dropdown-toggle regular-12 pr-2"
								type="button"
								id="dropdownStyles"
								data-toggle="dropdown"
								aria-haspopup="true"
								aria-expanded="false"
							>
								<span className="pr-2"> {notification.notificationFilterName.value}</span>
							</button>
							<div className="dropdown-menu" aria-labelledby="dropdownStyles">
								<ul>
									<li>
										<span onClick={() => getFilterData(FILTER_ACTION.important)}>
											<img
												src={`${process.env.PUBLIC_URL}/icons/important-flag-red.svg`}
												alt="important"
												className="mr-1"
											/>{' '}
											Important{' '}
										</span>
									</li>
									<li>
										<span onClick={() => getFilterData(FILTER_ACTION.all)}>
											Read &amp; Unread
										</span>
									</li>
									<li>
										<span onClick={() => getFilterData(FILTER_ACTION.read)}>Read</span>
									</li>
									<li>
										<span onClick={() => getFilterData(FILTER_ACTION.unread)}>
											Unread
										</span>
									</li>
								</ul>
							</div>
						</div>
						{notification.notificationTabName !== ACTIVE_TAB_OBJ.all.value ? (
							<button
								className="read-btn regular-12"
								onClick={() => markAllByFrontEndCategory()}
							>
								{' '}
								Mark all as read
							</button>
						) : (
							''
						)}
					</div>
				</div>
				<div className="notification-body">
					{notification?.notifications?.length > 0 ? (
						<div className="all-single-items" onScroll={onScrollToEnd}>
							{notification?.notifications?.map((notification, i) => {
								return (
									<NotificationItem
										item={notification}
										markRead={markRead}
										markImportant={markImportant}
										toggleModel={toggleModel}
										isToggleModel={isToggleModel}
										key={i}
										memberList={memberList}
									/>
								);
							})}
							{isLoading ? (
								<CreateSkeletons iterations={5}>
									<NotificationSkeleton />
								</CreateSkeletons>
							) : (
								''
							)}
						</div>
					) : (
						<div className="empty">
							<p>No notification found</p>
						</div>
					)}
				</div>
			</div>
		</LoaderComponent>
	);
};

export default Notification;
